import classNames from "classnames";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useEffect, useRef } from "react";

import "./styles.css";

const Embed = Quill.import("blots/embed") as any;

class PlaceholderBlot extends Embed {
  static blotName = "placeholder";
  static tagName = "span";
  static className = "placeholder";

  static create(value: string) {
    const node = super.create(value) as HTMLElement;
    node.textContent = `{${value}}`;
    return node;
  }

  static value(node: HTMLElement) {
    const match = (node.textContent ?? "").match(/^\{(.+)\}$/);
    return match ? match[1] : "";
  }
}

Quill.register(PlaceholderBlot, true);

const PLACEHOLDER_WRAPPER_RE = new RegExp(
  `<span class="placeholder">\\uFEFF*<span[^>]*?contenteditable="false"[^>]*?>([\\s\\S]*?)<\\/span>\\uFEFF*<\\/span>`,
  "g",
);

function serializeHtml(html: string): string {
  return html.replace(
    PLACEHOLDER_WRAPPER_RE,
    (_, inner) => `<span class="placeholder">${inner}</span>`,
  );
}

const ALLOWED_FORMATS = [
  "bold",
  "italic",
  "underline",
  "link",
  "background",
  "placeholder",
];

export interface RichTextEditorProps {
  name: string;
  label?: string;
  required?: boolean;
  editorRef?: React.Ref<HTMLDivElement>;
  error?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholderKeys?: string[];
}

export function RichTextEditor({
  name,
  label,
  required,
  editorRef,
  error = "",
  value = "",
  onChange = () => {},
  onBlur = () => {},
  placeholderKeys = [],
}: RichTextEditorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastHtmlRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const toolbarEl = document.createElement("div");
    toolbarEl.innerHTML = buildToolbarHTML(placeholderKeys);
    const editorEl = document.createElement("div");
    wrapper.appendChild(toolbarEl);
    wrapper.appendChild(editorEl);

    const handlers: Record<string, (value?: string) => void> = {
      undo() {
        (quillRef.current as any)?.history.undo();
      },
      redo() {
        (quillRef.current as any)?.history.redo();
      },
    };
    if (placeholderKeys.length > 0) {
      handlers.placeholder = (selected?: string) => {
        const quill = quillRef.current;
        if (!quill || !selected) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "placeholder", selected, "user" as any);
        quill.setSelection(range.index + 1, 0, "silent" as any);
      };
    }

    const quill = new Quill(editorEl, {
      theme: "snow",
      placeholder: "Type or paste your content here!",
      formats: ALLOWED_FORMATS,
      modules: {
        toolbar: {
          container: toolbarEl,
          handlers,
        },
      },
    });

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value, "silent" as any);
    }
    lastHtmlRef.current = serializeHtml(quill.root.innerHTML);

    quill.on("text-change", () => {
      const html = serializeHtml(quill.root.innerHTML);
      lastHtmlRef.current = html;
      onChangeRef.current(html);
    });

    const handleBlur = () =>
      onBlurRef.current(serializeHtml(quill.root.innerHTML));
    quill.root.addEventListener("blur", handleBlur);

    quillRef.current = quill;

    return () => {
      quill.root.removeEventListener("blur", handleBlur);
      quillRef.current = null;
      wrapper.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    if (value === lastHtmlRef.current) return;
    lastHtmlRef.current = value;
    quill.clipboard.dangerouslyPasteHTML(value || "", "silent" as any);
  }, [value]);

  return (
    <div className="flex flex-col w-full">
      <label
        htmlFor={name}
        className={classNames(
          "transition-top duration-300",
          "text-default font-comfortaa",
          "text-base font-light capitalize",
        )}
      >
        {label}
        <span>{required ? "*" : ""}</span>
      </label>
      <div className="w-full border border-default rounded" ref={editorRef}>
        <div id={name} ref={wrapperRef} />
      </div>
      <p className="text-xs ml-1 mt-1 text-danger font-comfortaa">{error}</p>
    </div>
  );
}

function buildToolbarHTML(placeholderKeys: string[]): string {
  const groups: string[] = [];

  groups.push(
    `<span class="ql-formats">` +
      `<button class="ql-undo" type="button" aria-label="Undo">&#x21B6;</button>` +
      `<button class="ql-redo" type="button" aria-label="Redo">&#x21B7;</button>` +
      `</span>`,
  );

  groups.push(
    `<span class="ql-formats">` +
      `<button class="ql-bold" type="button"></button>` +
      `<button class="ql-italic" type="button"></button>` +
      `<button class="ql-underline" type="button"></button>` +
      `</span>`,
  );

  groups.push(
    `<span class="ql-formats">` +
      `<button class="ql-link" type="button"></button>` +
      `<button class="ql-background" type="button" value="#FEF08A"></button>` +
      `</span>`,
  );

  if (placeholderKeys.length > 0) {
    const options = placeholderKeys
      .map((key) => `<option value="${key}">${key}</option>`)
      .join("");
    groups.push(
      `<span class="ql-formats">` +
        `<select class="ql-placeholder">` +
        `<option selected></option>` +
        options +
        `</select>` +
        `</span>`,
    );
  }

  return groups.join("");
}
